/**
 * DIM-ARROW-BLOCK-GEOMETRY (SL-11 finding F7) — the real SHAPE geometry for the
 * on-demand-synthesized built-in AutoCAD arrowhead BLOCKs.
 *
 * `stampDimensionDStyleArrowheadHandles` resolves a DIMENSION DSTYLE
 * `dimblk`/`dimblk1`/`dimblk2` override (DIMSTYLE codes 342/343/344) that names a
 * built-in AutoCAD arrow (`DOT`, `_DOT`, `OPEN30`, `_ARCHTICK`, …) with NO
 * user-declared block by SYNTHESIZING the arrow's canonical `_<NAME>`
 * BLOCK_RECORD + BLOCK on demand (mirrors ezdxf `set_arrow_handle` →
 * `ARROWS.create_block`, and AutoCAD auto-creating the arrow block on use).
 * Historically that BLOCK carried the correct NAME + handle but an EMPTY
 * `entities: []` body, so a `dimblk` override naming a built-in arrow round-tripped
 * conformantly (name resolves, ezdxf audit 0/0) but had NO rendered arrowhead
 * shape — the F7 gap.
 *
 * This module is a FAITHFUL PORT of ezdxf 1.4.3 `render/arrows.py` `ARROWS`
 * (`arrow_shape` + the per-class `render`) for the `__acad__` set, so the
 * synthesized `_<NAME>` block contains the SAME dot / tick / oblique / open /
 * closed / box / datum / origin / integral geometry `ARROWS.create_block` injects.
 * AutoCAD and any conformant reader (ezdxf) then draw + plot the correct
 * arrowhead. The `dstyleDimblk.conformance.test.ts` + `builtinArrowGeometry.test.ts`
 * oracle gates assert the emitted entities match ezdxf `ARROWS.create_block`
 * exactly (entity type sequence + coordinates) while keeping the strict
 * readfile+audit at 0/0 (AC1032).
 *
 * Geometry is authored for the base size 1, insert (0,0), rotation 0 — exactly the
 * arguments `ARROWS.create_block(doc.blocks, name)` passes to `arrow_shape` (which
 * additionally scales `DOTSMALL` by 0.25 and `SMALL` by 0.5). AutoCAD scales the
 * unit block by `dimasz` at draw time via the arrow BLOCK insertion, so the block
 * body is unit-sized exactly as ezdxf stores it.
 *
 * Every entity carries `color 0` (BYBLOCK) + `linetype "BYBLOCK"` on layer "0",
 * mirroring `arrow.render(block, dxfattribs={"color": 0, "linetype": "BYBLOCK"})`.
 *
 * Source of truth: ezdxf 1.4.3 `render/arrows.py` (`_Arrows.arrow_shape`,
 * `open_arrow`, the arrow `render` methods) + Autodesk DXF R2018. AutoCAD MCP is
 * BANNED.
 */
import type {
  DxfCommonEntityProps,
  DxfEntity,
  DxfGroup,
} from "@dxf-ai-editor/dxf-io";

import { BUILTIN_ACAD_ARROW_NAMES } from "./builtinArrowNames";

/**
 * ezdxf `render/arrows.py` `DEFAULT_ARROW_ANGLE` — the enclosing angle (degrees)
 * of the `OPEN` / `CLOSED` / `CLOSEDBLANK` arrow flanks (`open_arrow`'s `angle`).
 */
const DEFAULT_ARROW_ANGLE = 18.924644;
/** ezdxf `Integral.radius = size * 0.3535534` (size 1). */
const INTEGRAL_RADIUS = 0.3535534;
/** ezdxf `DatumTriangle` half-height `d = 0.577350269 * size` (= tan 30°, size 1). */
const DATUM_D = 0.577350269;

/**
 * Collaborators the arrow-block synthesizer threads in so each emitted child
 * entity gets a doc-wide collision-free group-5 handle and the BLOCK_RECORD as its
 * group-330 owner (a block-child entity is owned by its BLOCK_RECORD, verified
 * against ezdxf — the arrow's LINE/CIRCLE/… all carry `owner == block_record_handle`).
 */
export interface ArrowGeometryContext {
  /** Allocate the next doc-wide collision-free HEX handle (folded into the census). */
  mintHandle(): string;
  /** The synthesized arrow BLOCK_RECORD handle — the group-330 owner of each child. */
  readonly ownerHandle: string;
}

/** A 2D point in the unit arrow shape (all arrow geometry is planar, z = 0). */
type Pt2 = readonly [number, number];

/**
 * Format a number for a raw DXF group value IDENTICALLY to the dxf-io writer's
 * `formatDxfNumber` (`toFixed(12)` then trailing-zero trim) — used only for the
 * SOLID raw-group body (2D solids are not in the typed supported-entity set, so
 * they ship as an `unsupported-entity` whose groups are emitted verbatim). Keeping
 * the same formatting means a synthesized filled-arrow SOLID is byte-shaped like
 * every other numeric group and ezdxf reads it back clean.
 */
function fmtNum(value: number): string {
  return value.toFixed(12).replace(/\.?0+$/, "");
}

/** The BYBLOCK common props every arrow child carries (see module doc). */
function arrowCommon(ctx: ArrowGeometryContext): DxfCommonEntityProps {
  return {
    handle: ctx.mintHandle(),
    ownerHandle: ctx.ownerHandle,
    layer: "0",
    linetype: "BYBLOCK",
    colorNumber: 0,
    // Conformant export writes group 8 on every entity (matches ezdxf's explicit
    // `8 0` on each arrow child) — see the ACDBENTITY layer emitter.
    forceLayerEmit: true,
  };
}

function makeLine(ctx: ArrowGeometryContext, start: Pt2, end: Pt2): DxfEntity {
  return {
    kind: "supported-entity",
    type: "LINE",
    common: arrowCommon(ctx),
    subclassMarkers: [],
    extraGroups: [],
    geometry: {
      start: { x: start[0], y: start[1], z: 0 },
      end: { x: end[0], y: end[1], z: 0 },
    },
  };
}

function makeCircle(ctx: ArrowGeometryContext, center: Pt2, radius: number): DxfEntity {
  return {
    kind: "supported-entity",
    type: "CIRCLE",
    common: arrowCommon(ctx),
    subclassMarkers: [],
    extraGroups: [],
    geometry: { center: { x: center[0], y: center[1], z: 0 }, radius },
  };
}

function makeArc(
  ctx: ArrowGeometryContext,
  center: Pt2,
  radius: number,
  startAngle: number,
  endAngle: number,
): DxfEntity {
  return {
    kind: "supported-entity",
    type: "ARC",
    common: arrowCommon(ctx),
    subclassMarkers: [],
    extraGroups: [],
    geometry: {
      center: { x: center[0], y: center[1], z: 0 },
      radius,
      startAngle,
      endAngle,
    },
  };
}

/** A vertex of an arrow LWPOLYLINE — `bulge` present only for the round dot arrows. */
interface ArrowVertex {
  readonly p: Pt2;
  readonly bulge?: number;
}

function makeLwPolyline(
  ctx: ArrowGeometryContext,
  verts: readonly ArrowVertex[],
  closed: boolean,
  constantWidth?: number,
): DxfEntity {
  return {
    kind: "supported-entity",
    type: "LWPOLYLINE",
    common: arrowCommon(ctx),
    subclassMarkers: [],
    extraGroups: [],
    geometry: {
      vertices: verts.map((v) => ({
        x: v.p[0],
        y: v.p[1],
        ...(v.bulge !== undefined && v.bulge !== 0 ? { bulge: v.bulge } : {}),
      })),
      closed,
      vertexCount: verts.length,
      ...(constantWidth !== undefined && constantWidth !== 0
        ? { constantWidth }
        : {}),
    },
  };
}

/**
 * A 2D SOLID (`AcDbTrace`) — ezdxf's filled arrows (`BOXFILLED`, `DATUMFILLED`)
 * emit `add_solid`. SOLID is not in the typed supported-entity union, so it ships
 * as an `unsupported-entity` whose raw group list mirrors ezdxf's SOLID export
 * (`100 AcDbTrace` + the four `10/11/12/13` corner triples) verbatim; the writer
 * emits `entity.groups` as-is. A 3-corner triangle (`DATUMFILLED`) repeats the
 * third corner as the fourth, exactly as ezdxf's `add_solid` does.
 */
function makeSolid(
  ctx: ArrowGeometryContext,
  corners: readonly [Pt2, Pt2, Pt2, Pt2],
): DxfEntity {
  const common = arrowCommon(ctx);
  const groups: DxfGroup[] = [
    { code: 0, value: "SOLID" },
    { code: 5, value: common.handle! },
    { code: 330, value: common.ownerHandle! },
    { code: 100, value: "AcDbEntity" },
    { code: 8, value: "0" },
    { code: 6, value: "BYBLOCK" },
    { code: 62, value: "0" },
    { code: 100, value: "AcDbTrace" },
  ];
  // vtx0→10/20/30, vtx1→11/21/31, vtx2→12/22/32, vtx3→13/23/33 (ezdxf SOLID order).
  const xCodes = [10, 11, 12, 13];
  const yCodes = [20, 21, 22, 23];
  const zCodes = [30, 31, 32, 33];
  corners.forEach((c, i) => {
    groups.push({ code: xCodes[i]!, value: fmtNum(c[0]) });
    groups.push({ code: yCodes[i]!, value: fmtNum(c[1]) });
    groups.push({ code: zCodes[i]!, value: fmtNum(0) });
  });
  return { kind: "unsupported-entity", type: "SOLID", groups };
}

/**
 * ezdxf `forms.open_arrow(size=1, angle)` — the three flank vertices of an open
 * arrow `<`: upper end `(-1, h)`, tip `(0, 0)`, lower end `(-1, -h)` where
 * `h = sin(angle/2)`. Shared by `OPEN`/`OPEN30`/`OPEN90` (open polyline) and
 * `CLOSED`/`CLOSEDBLANK` (closed polyline).
 */
function openArrowPoints(angleDeg: number): readonly [Pt2, Pt2, Pt2] {
  const h = Math.sin((angleDeg / 2) * (Math.PI / 180));
  return [
    [-1, h],
    [0, 0],
    [-1, -h],
  ];
}

/** `OPEN` / `OPEN30` / `OPEN90` — open polyline flanks + the (0,0)→(-1,0) stem line. */
function openArrow(ctx: ArrowGeometryContext, angleDeg: number): DxfEntity[] {
  const pts = openArrowPoints(angleDeg);
  return [
    makeLwPolyline(ctx, pts.map((p) => ({ p })), false),
    makeLine(ctx, [0, 0], [-1, 0]),
  ];
}

/**
 * Per-arrow geometry generators keyed by the UPPERCASE AutoCAD arrow name (the
 * `_<NAME>` block's name minus its leading `_`). Each mirrors the matching ezdxf
 * `render/arrows.py` class `render` at size 1 (the `arrow_shape` per-name scale for
 * `DOTSMALL`/`SMALL` is folded into the literals below).
 */
const ARROW_GEOMETRY: Record<string, (ctx: ArrowGeometryContext) => DxfEntity[]> = {
  // ArchTick — ObliqueStroke shape as an LWPOLYLINE with const_width = size*0.15.
  ARCHTICK: (ctx) => [makeArchTick(ctx)],
  // Box (BOXBLANK) — closed unit square + the (-0.5,0)→(-1,0) stem line.
  BOXBLANK: (ctx) => [
    makeLwPolyline(
      ctx,
      [
        { p: [-0.5, -0.5] },
        { p: [0.5, -0.5] },
        { p: [0.5, 0.5] },
        { p: [-0.5, 0.5] },
      ],
      true,
    ),
    makeLine(ctx, [-0.5, 0], [-1, 0]),
  ],
  // BoxFilled — SOLID square (ezdxf corner order v0,v1,v3,v2) + the stem line.
  BOXFILLED: (ctx) => [
    makeSolid(ctx, [
      [-0.5, -0.5],
      [0.5, -0.5],
      [-0.5, 0.5],
      [0.5, 0.5],
    ]),
    makeLine(ctx, [-0.5, 0], [-1, 0]),
  ],
  // ClosedArrow — closed open-arrow polyline + the tip→base center stem line.
  CLOSED: (ctx) => {
    const pts = openArrowPoints(DEFAULT_ARROW_ANGLE);
    return [
      makeLwPolyline(ctx, pts.map((p) => ({ p })), true),
      makeLine(ctx, [0, 0], [-1, 0]),
    ];
  },
  // ClosedArrowBlank — the closed open-arrow polyline alone (no stem line).
  CLOSEDBLANK: (ctx) =>
    [makeLwPolyline(ctx, openArrowPoints(DEFAULT_ARROW_ANGLE).map((p) => ({ p })), true)],
  // DatumTriangle — closed triangle (upper, lower, connection).
  DATUMBLANK: (ctx) =>
    [
      makeLwPolyline(
        ctx,
        [{ p: [0, DATUM_D] }, { p: [0, -DATUM_D] }, { p: [-1, 0] }],
        true,
      ),
    ],
  // DatumTriangleFilled — filled triangle (3 corners; the 4th repeats the 3rd).
  DATUMFILLED: (ctx) => [
    makeSolid(ctx, [
      [0, DATUM_D],
      [0, -DATUM_D],
      [-1, 0],
      [-1, 0],
    ]),
  ],
  // Dot — the (-0.5,0)→(-1,0) stem line, then the filled round dot (DotSmall @ size 1).
  DOT: (ctx) => [makeLine(ctx, [-0.5, 0], [-1, 0]), ...roundDot(ctx, 0.5)],
  // CircleBlank — an unfilled circle r=0.5 + the (-0.5,0)→(-1,0) stem line.
  DOTBLANK: (ctx) => [makeCircle(ctx, [0, 0], 0.5), makeLine(ctx, [-0.5, 0], [-1, 0])],
  // DotSmall — the filled round dot alone, at size*0.25 (radius 0.125).
  DOTSMALL: (ctx) => roundDot(ctx, 0.125),
  // Integral — two quarter arcs of radius 0.3535534 flanking the dimension line.
  INTEGRAL: (ctx) => [
    makeArc(ctx, [-INTEGRAL_RADIUS, 0], INTEGRAL_RADIUS, -90, 0),
    makeArc(ctx, [INTEGRAL_RADIUS, 0], INTEGRAL_RADIUS, 90, 180),
  ],
  // NoneStroke — no geometry (a single-vertex shape that renders nothing).
  NONE: () => [],
  // ObliqueStroke — a single slanted tick line.
  OBLIQUE: (ctx) => [makeLine(ctx, [-0.5, -0.5], [0.5, 0.5])],
  OPEN: (ctx) => openArrow(ctx, DEFAULT_ARROW_ANGLE),
  OPEN30: (ctx) => openArrow(ctx, 30),
  OPEN90: (ctx) => openArrow(ctx, 90),
  // Origin — a circle r=0.5 + the center→base (0,0)→(-1,0) stem line.
  ORIGIN: (ctx) => [makeCircle(ctx, [0, 0], 0.5), makeLine(ctx, [0, 0], [-1, 0])],
  // Origin2 — concentric circles r=0.5 & r=0.25 + the (-0.5,0)→(-1,0) stem line.
  ORIGIN2: (ctx) => [
    makeCircle(ctx, [0, 0], 0.5),
    makeCircle(ctx, [0, 0], 0.25),
    makeLine(ctx, [-0.5, 0], [-1, 0]),
  ],
  // Circle (SMALL, dot_smallblank @ size*0.5) — a lone unfilled circle r=0.25.
  SMALL: (ctx) => [makeCircle(ctx, [0, 0], 0.25)],
};

/**
 * ezdxf `DotSmall.render` — a filled round dot as a closed 2-vertex LWPOLYLINE
 * whose vertices each carry bulge 1 (two semicircles) and `const_width = radius`.
 * The two dot flavours differ only by radius (DOT 0.5, DOTSMALL 0.125).
 */
function roundDot(ctx: ArrowGeometryContext, radius: number): DxfEntity[] {
  const d = radius / 2;
  return [
    makeLwPolyline(
      ctx,
      [
        { p: [-d, 0], bulge: 1 },
        { p: [d, 0], bulge: 1 },
      ],
      true,
      radius,
    ),
  ];
}

/** ezdxf `ArchTick.render` — the oblique tick as a wide (const_width) open polyline. */
function makeArchTick(ctx: ArrowGeometryContext): DxfEntity {
  return makeLwPolyline(ctx, [{ p: [-0.5, -0.5] }, { p: [0.5, 0.5] }], false, 0.15);
}

/**
 * Generate the child entities of a built-in arrow's `_<NAME>` BLOCK, mirroring
 * ezdxf `ARROWS.create_block(blocks, name)` for the `__acad__` set. `recordName` is
 * the canonical `_<NAME>` block-record name (from
 * {@link builtinArrowBlockRecordName}); the leading `_` is stripped to key the
 * per-name generator. A name that is not a built-in AutoCAD arrow — or `NONE`,
 * whose ezdxf block is intentionally empty — yields an empty entity list (the
 * BLOCK is still synthesized, so the DSTYLE `1005` resolves).
 */
export function buildBuiltinArrowBlockEntities(
  recordName: string,
  ctx: ArrowGeometryContext,
): DxfEntity[] {
  const bare = (recordName.startsWith("_") ? recordName.slice(1) : recordName).toUpperCase();
  if (!BUILTIN_ACAD_ARROW_NAMES.has(bare)) return [];
  const generator = ARROW_GEOMETRY[bare];
  return generator ? generator(ctx) : [];
}
